import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";

export const Target = ({ className = "mx-auto" }) => {
  const targetRef = useRef(null);
  const circlesRef = useRef<(SVGCircleElement | null)[]>([]);

  useGSAP(() => {
    const circles = circlesRef.current.filter(Boolean);

    if (!circles.length) return;

    // Create infinite loop timeline
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 0.5 });

    // Set initial state - all circles scaled to 0 from center
    gsap.set(circles, {
      scale: 0,
      transformOrigin: "center",
      opacity: 0.8,
    });

    // Animate circles appearing from center outward with stagger
    tl.to(circles, {
      scale: 1,
      opacity: 1,
      duration: 1.5,
      stagger: 0.2,
      ease: "back.out(1.7)",
    })

      // Add a pulsing effect to the center circle
      .to(
        circles[3],
        {
          // Center circle
          scale: 1.2,
          duration: 0.8,
          yoyo: true,
          repeat: 2,
          ease: "sine.inOut",
        },
        "-=1"
      )

      // Add a subtle rotation to the whole target
      .to(
        targetRef.current,
        {
          rotation: 360,
          duration: 3,
          ease: "none",
        },
        "-=2"
      )

      // Hold the animation
      .to({}, { duration: 1 })

      // Fade out all circles
      .to(circles, {
        scale: 0,
        opacity: 0,
        duration: 1,
        stagger: 0.1,
        ease: "power2.in",
      })

      // Reset rotation
      .set(targetRef.current, { rotation: 0 });
  });

  return (
    <svg
      ref={targetRef}
      id="animated-target"
      viewBox="0 0 200 200"
      width={150}
      height={150}
      className={className}
    >
      <defs>
        <linearGradient
          id="linear-gradient"
          x1="100"
          y1="175.96"
          x2="100"
          y2="10.63"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#f89c6b" />
          <stop offset="1" stopColor="#cdcd81" />
        </linearGradient>
        <linearGradient
          id="linear-gradient-2"
          x1="97.65"
          y1="246.41"
          x2="101.14"
          y2="28.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0d2949" />
          <stop offset="1" stopColor="#52b773" />
        </linearGradient>
        <linearGradient
          id="linear-gradient-3"
          x1="100"
          y1="57.22"
          x2="100"
          y2="142.16"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#43b0da" />
          <stop offset="1" stopColor="#52b773" />
        </linearGradient>
      </defs>

      {/* Outermost circle - Orange gradient */}
      <circle
        ref={(el) => {
          circlesRef.current[0] = el;
        }}
        style={{ fill: "url(#linear-gradient)" }}
        cx="100"
        cy="100"
        r="91.51"
      />

      {/* Second circle - Blue/Green gradient */}
      <circle
        ref={(el) => {
          circlesRef.current[1] = el;
        }}
        style={{ fill: "url(#linear-gradient-2)" }}
        cx="100"
        cy="100"
        r="70.6"
      />

      {/* Third circle - Blue gradient */}
      <circle
        ref={(el) => {
          circlesRef.current[2] = el;
        }}
        style={{ fill: "url(#linear-gradient-3)" }}
        cx="100"
        cy="100"
        r="46.47"
      />

      {/* Center bullseye - Dark blue */}
      <circle
        ref={(el) => {
          circlesRef.current[3] = el;
        }}
        style={{ fill: "#0d2949" }}
        cx="100"
        cy="100"
        r="15.12"
      />
    </svg>
  );
};
